import { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import store from '../../../store';
import { updateUserDetails } from '../../../store/user/duck';
import { LOADING } from '../../../constants/phase';

/**
 * 我的溝通風格 × 對方的 DISC — the 16 pairings behind DiscChemistryCard.
 *
 * Shared by 招募 IDEA (the other person is a candidate) and Smart IDEA's
 * 溝通方式 tab (the other person is a client). The adviser's own letter is one
 * fact about the adviser — `users.personality` — so both surfaces read and
 * write the same field: set it in one and the other already knows.
 *
 * The pairing's `dynamic` label describes two temperaments meeting and holds in
 * either context, so it lives once, in the recruit table. `blindSpot` and
 * `strategy` are advice for a specific conversation — a career talk is not a
 * sales meeting — so each mode has its own.
 */

export type AdvisorDiscType = 'D' | 'I' | 'S' | 'C';

/** Which conversation the pairing advice is for. */
export type DiscChemistryMode = 'recruit' | 'business';

export const ADVISOR_DISC_TYPES: AdvisorDiscType[] = ['D', 'I', 'S', 'C'];

// The adviser's own DISC is users.personality — the field 我的資料 edits
// (§3.1). The old device-only copy is dropped once.
try {
  window.localStorage.removeItem('clientv2-advisor-disc');
} catch (_error) {
  // private mode
}

const discOf = (value: unknown): AdvisorDiscType | null =>
  value === 'D' || value === 'I' || value === 'S' || value === 'C' ? value : null;

export const readAdvisorDisc = (): AdvisorDiscType | null =>
  discOf(_.get(store.getState(), 'user.userDetails.personality'));

export const writeAdvisorDisc = (disc: AdvisorDiscType | null): void => {
  store.dispatch(updateUserDetails({ personality: disc || '' }));
};

/**
 * The adviser's style, following the store; a pick shows at once and settles on
 * the server's answer. While the pick is saving the store still holds the old
 * letter (UPDATE_USER itself notifies subscribers), so the store is not followed
 * until the save settles: success brings the new letter, failure the old one.
 */
export const useAdvisorDisc = (): [AdvisorDiscType | null, (d: AdvisorDiscType | null) => void] => {
  const [disc, setDisc] = useState<AdvisorDiscType | null>(readAdvisorDisc);
  const saving = useRef(false);
  useEffect(
    () =>
      store.subscribe(() => {
        if (saving.current && _.get(store.getState(), 'user.updatePhase') === LOADING) return;
        saving.current = false;
        setDisc(readAdvisorDisc());
      }),
    []
  );
  const update = (next: AdvisorDiscType | null) => {
    saving.current = true;
    setDisc(next);
    writeAdvisorDisc(next);
  };
  return [disc, update];
};

export type DiscChemistryInfo = {
  dynamic: string;
  blindSpot: string;
  strategy: string;
};

type ChemistryLocale = 'zh-Hant-HK' | 'zh' | 'en';
type PairAdvice = Pick<DiscChemistryInfo, 'blindSpot' | 'strategy'>;

const RECRUIT_CHEMISTRY: Record<ChemistryLocale, Record<string, DiscChemistryInfo>> = {
  'zh-Hant-HK': {
    D_D: {
      dynamic: '強者對決 · 平等合作',
      blindSpot: '切忌說教、命令或試圖掌控全局；果斷決策型候選人極為敏感於被支配，容易演變成互不相讓的爭奪。',
      strategy:
        '給予充分自主權與合夥人格局！直接聚焦最高回報、晉升天花板與自由決策權，讓他感到是與你平起平坐的事業盟友。'
    },
    D_I: {
      dynamic: '果斷魄力 ✕ 熱情感染',
      blindSpot: '切忌過於嚴肅冷峻、只講 KPI 與數字；這會迅速澆熄熱情分享型候選人的分享慾與面談熱情。',
      strategy: '多給予公開讚賞與舞台期待！肯定他的人脈磁性與表達魅力，再引導他了解如何將熱情轉換為高額回報。'
    },
    D_S: {
      dynamic: '雷厲風行 ✕ 溫和謹慎',
      blindSpot:
        '切忌節奏過快、步步進逼或施加決策壓力；穩健守護型候選人極度重視安全感，強推會讓他因恐慌而封閉並藉故拒絕。',
      strategy: '請把語速放慢一半！先關心他的現狀與家庭感受，反覆強調團隊的手把手陪同與穩定過渡期，切忌逼他當場做決定。'
    },
    D_C: {
      dynamic: '宏觀願景 ✕ 理性求證',
      blindSpot: '切忌只談大方向大願景而跳過邏輯推導；精明分析型候選人會認為你誇大其詞、缺乏嚴謹依據。',
      strategy: '用客觀數據與制度說話！準備好具體合約細節、佣金分配表與新人育成率數據，給他足夠時間冷靜分析與推演。'
    },
    I_D: {
      dynamic: '熱情激勵 ✕ 結果導向',
      blindSpot: '切忌漫無邊際閒聊鋪陳或過多情感寒暄；果斷決策型候選人會感到效率低下、缺乏重點，甚至質疑你的領導能力。',
      strategy: '3 分鐘內直奔主題！用乾脆俐落的語言說清楚：能賺多少、晉升需要幾個人、天花板有多高，用專業魄力贏得尊重。'
    },
    I_I: {
      dynamic: '惺惺相惜 · 火花四濺',
      blindSpot: '容易聊得極為投契愉快，但雙方都缺乏落實具體細節的自律，容易演變成「聊完很興奮，過後沒下文」。',
      strategy:
        '享受熱烈氛圍之餘，務必做好締結！在對話情緒最高點敲定具體下一步（例如：約定下一次會面或執照培訓具體日期）。'
    },
    I_S: {
      dynamic: '熱情引領 ✕ 溫和信賴',
      blindSpot: '切忌描繪過於激進的變革或把未來形容得太具挑戰；穩健守護型候選人會懷疑自己能力不足、無法適應環境變化。',
      strategy: '聚焦於「團隊陪伴」與「安全感」！強調他不是孤軍奮戰，團隊有溫暖的夥伴與經驗豐富的導師隨時支援。'
    },
    I_C: {
      dynamic: '感性願景 ✕ 縝密分析',
      blindSpot:
        '切忌過度發揮感性、講虛無縹緲的夢想或畫大餅；精明分析型候選人天生理性懷疑，會覺得你浮誇不實、缺乏專業度。',
      strategy: '收起情緒化表達，換上事實清單！拿出具體數據、規章流程與書面資料，讓他自己看清楚每個細節與條件。'
    },
    S_D: {
      dynamic: '包容耐心 ✕ 銳意進取',
      blindSpot: '切忌說話過於客套迂迴、吞吞吐吐；果斷決策型候選人會認為你缺乏魄力與決斷力，無法帶領他衝擊更高目標。',
      strategy: '展現堅定與篤定！少用「或許、可能」，明確告訴他你能為他調配的團隊資源與平台跳板，讓他看見你的可靠底氣。'
    },
    S_I: {
      dynamic: '溫和傾聽 ✕ 活力四射',
      blindSpot: '切忌反應過於平淡冷靜、節奏過慢；熱情分享型候選人需要即時的情緒價值，太安靜會讓他懷疑你是否認可他。',
      strategy: '提高對話能量，給予熱情反饋！對他的特質與想法給予積極肯定，讚美他的社交魅力，描繪他在團隊受歡迎的場景。'
    },
    S_S: {
      dynamic: '彼此理解 · 溫和共鳴',
      blindSpot: '雙方都太過客氣、互相顧慮，誰都不好意思推進關鍵話題，導致招募週期無限拖長、陷入停滯。',
      strategy: '主動承擔推進責任！在合適時機主動提議：「下週二我陪你一起見見總監，我全程在場」，以陪伴消除他的猶豫。'
    },
    S_C: {
      dynamic: '細緻周到 ✕ 嚴謹求實',
      blindSpot: '對話容易過於謹慎保守、節奏拖沓，雙方都在觀望等待，缺乏促成改變的推動力。',
      strategy: '發揮你的同理心與耐心，逐項解答他的疑慮，並在最後提供條理清晰的下一步步驟指南。'
    },
    C_D: {
      dynamic: '專業嚴密 ✕ 聚焦成果',
      blindSpot: '切忌拘泥於細碎條款或繁瑣流程；果斷決策型候選人關注大局與結果，瑣碎細節會讓他感到不耐煩與受拘束。',
      strategy: '只講宏觀框架與核心權益！突出制度給予的自主空間與回報上限，行政與合規細節留待後續落實。'
    },
    C_I: {
      dynamic: '理性邏輯 ✕ 情感共鳴',
      blindSpot:
        '切忌通篇冷靜分析制度與規則、忽視人情味；熱情分享型候選人重視感覺與氛圍，冰冷的對話會讓他覺得團隊缺乏溫度。',
      strategy:
        '展現親和力與熱情！多肯定他的溝通優勢，描繪愉快的團隊合作氛圍，把嚴謹的制度包裝為「保護新人成長的後盾」。'
    },
    C_S: {
      dynamic: '井然有序 ✕ 追求安定',
      blindSpot: '切忌只講生硬法規與考核標準，忽略穩健守護型候選人內心的孤獨感與對人際關係的顧慮。',
      strategy: '用清晰完善的制度賦予他最大的「確定性」！詳細說明每一步都有標準作業流程與導師守護，消除對未知的恐懼。'
    },
    C_C: {
      dynamic: '深度思考 · 專業推演',
      blindSpot: '容易雙方陷入無休止的比對分析與細節爭論，造成「分析癱瘓」，遲遲無法採取行動。',
      strategy: '主動設定行動停損點！「我們已經梳理了所有可能情況，現在以最低成本驗證的方式就是參加一次團隊交流體驗。」'
    }
  },
  zh: {
    D_D: {
      dynamic: '强者对决 · 平等合作',
      blindSpot: '切忌说教、命令或试图掌控全局；果断决策型候选人极为敏感于被支配，容易演变成互不相让的争夺。',
      strategy:
        '给予充分自主权与合伙人格局！直接聚焦最高回报、晋升天花板与自由决策权，让他感到是与你平起平坐的事业盟友。'
    },
    D_I: {
      dynamic: '果断魄力 ✕ 热情感染',
      blindSpot: '切忌过于严肃冷峻、只讲 KPI 与数字；这会迅速浇熄热情分享型候选人的分享欲与面谈热情。',
      strategy: '多给予公开赞赏与舞台期待！肯定他的人脉磁性与表达魅力，再引导他了解如何将热情转换为高额回报。'
    },
    D_S: {
      dynamic: '雷厉风行 ✕ 温和谨慎',
      blindSpot:
        '切忌节奏过快、步步进逼或施加决策压力；稳健守护型候选人极度重视安全感，强推会让他因恐慌而封闭并藉故拒绝。',
      strategy: '请把语速放慢一半！先关心他的现状与家庭感受，反复强调团队的手把手陪同与稳定过渡期，切忌逼他当场做决定。'
    },
    D_C: {
      dynamic: '宏观愿景 ✕ 理性求证',
      blindSpot: '切忌只谈大方向大愿景而跳过逻辑推导；精明分析型候选人会认为你夸大其词、缺乏严谨依据。',
      strategy: '用客观数据与制度说话！准备好具体合约细节、佣金分配表与新人育成率数据，给他足够时间冷静分析与推演。'
    },
    I_D: {
      dynamic: '热情激励 ✕ 结果导向',
      blindSpot: '切忌漫无边际闲聊铺陈或过多情感寒暄；果断决策型候选人会感到效率低下、缺乏重点，甚至质疑你的领导能力。',
      strategy: '3 分钟内直奔主题！用干脆俐落的语言说清楚：能赚多少、晋升需要几个人、天花板有多高，用专业魄力赢得尊重。'
    },
    I_I: {
      dynamic: '惺惺相惜 · 火花四溅',
      blindSpot: '容易聊得极为投契愉快，但双方都缺乏落实具体细节的自律，容易演变成「聊完很兴奋，过后没下文」。',
      strategy:
        '享受热烈氛围之余，务必做好缔结！在对话情绪最高点敲定具体下一步（例如：约定下一次会面或执照培训具体日期）。'
    },
    I_S: {
      dynamic: '热情引领 ✕ 温和信赖',
      blindSpot: '切忌描绘过于激进的变革或把未来形容得太具挑战；稳健守护型候选人会怀疑自己能力不足、无法适应环境变化。',
      strategy: '聚焦于「团队陪伴」与「安全感」！强调他不是孤军奋战，团队有温暖的伙伴与经验丰富的导师随时支援。'
    },
    I_C: {
      dynamic: '感性愿景 ✕ 缜密分析',
      blindSpot:
        '切忌过度发挥感性、讲虚无缥缈的梦想或画大饼；精明分析型候选人天生理性怀疑，会觉得你浮夸不实、缺乏专业度。',
      strategy: '收起情绪化表达，换上事实清单！拿出具体数据、规章流程与书面资料，让他自己看清楚每个细节与条件。'
    },
    S_D: {
      dynamic: '包容耐心 ✕ 锐意进取',
      blindSpot: '切忌说话过于客套迂回、吞吞吐吐；果断决策型候选人会认为你缺乏魄力与决断力，无法带领他冲击更高目标。',
      strategy: '展现坚定与笃定！少用「或许、可能」，明确告诉他你能为他调配的团队资源与平台跳板，让他看见你的可靠底气。'
    },
    S_I: {
      dynamic: '温和倾听 ✕ 活力四射',
      blindSpot: '切忌反应过于平淡冷静、节奏过慢；热情分享型候选人需要即时的情绪价值，太安静会让他怀疑你是否认可他。',
      strategy: '提高对话能量，给予热情反馈！对他的特质与想法给予积极肯定，赞美他的社交魅力，描绘他在团队受欢迎的场景。'
    },
    S_S: {
      dynamic: '彼此理解 · 温和共鸣',
      blindSpot: '双方都太过客气、互相顾虑，谁都不好意思推进关键话题，导致招募周期无限拖长、陷入停滞。',
      strategy: '主动承担推进责任！在合适时机主动提议：「下周二我陪你一起见见总监，我全程在场」，以陪伴消除他的犹豫。'
    },
    S_C: {
      dynamic: '细致周到 ✕ 严谨求实',
      blindSpot: '对话容易过于谨慎保守、节奏拖沓，双方都在观望等待，缺乏促成改变的推动力。',
      strategy: '发挥你的同理心与耐心，逐项解答他的疑虑，并在最后提供条理清晰的下一步步骤指南。'
    },
    C_D: {
      dynamic: '专业严密 ✕ 聚焦成果',
      blindSpot: '切忌拘泥于细碎条款或繁琐流程；果断决策型候选人关注大局与结果，琐碎细节会让他感到不耐烦与受拘束。',
      strategy: '只讲宏观框架与核心权益！突出制度给予的自主空间与回报上限，行政与合规细节留待后续落实。'
    },
    C_I: {
      dynamic: '理性逻辑 ✕ 情感共鸣',
      blindSpot:
        '切忌通篇冷静分析制度与规则、忽视人情味；热情分享型候选人重视感觉与氛围，冰冷的对话会让他觉得团队缺乏温度。',
      strategy:
        '展现亲和力与热情！多肯定他的沟通优势，描绘愉快的团队合作氛围，把严谨的制度包装为「保护新人成长的后盾」。'
    },
    C_S: {
      dynamic: '井然有序 ✕ 追求安定',
      blindSpot: '切忌只讲生硬法规与考核标准，忽略稳健守护型候选人内心的孤独感与对人际关系的顾虑。',
      strategy: '用清晰完善的制度赋予他最大的「确定性」！详细说明每一步都有标准作业流程与导师守护，消除对未知的恐惧。'
    },
    C_C: {
      dynamic: '深度思考 · 专业推演',
      blindSpot: '容易双方陷入无休止的比对分析与细节争论，造成「分析瘫痪」，迟迟无法采取行动。',
      strategy: '主动设定行动停损点！「我们已经梳理了所有可能情况，现在以最低成本验证的方式就是参加一次团队交流体验。」'
    }
  },
  en: {
    D_D: {
      dynamic: 'High Stake Drive · Peer Respect',
      blindSpot:
        'Never lecture or dictate. Decisive Driver candidates resist control, turning the conversation into a power struggle.',
      strategy:
        'Give them peer autonomy. Focus directly on top-tier returns, career ceilings, and leadership independence.'
    },
    D_I: {
      dynamic: 'Decisive Force ✕ High Charisma',
      blindSpot:
        'Avoid being purely transactional with numbers. Cold pressure quickly dampens the enthusiasm of Enthusiastic Sharer candidates.',
      strategy:
        'Offer sincere praise and a vision of the spotlight. Value their magnetic networking, then channel it into rewards.'
    },
    D_S: {
      dynamic: 'Fast Pace ✕ Gentle Caution',
      blindSpot:
        'Do not rush or force commitments. Steady Guardian candidates need emotional safety; pressure pushes them away.',
      strategy:
        'Slow down your pace by half. Focus on family comfort, emphasize mentorship stability, and allow time to think.'
    },
    D_C: {
      dynamic: 'Macro Vision ✕ Logical Rigor',
      blindSpot:
        'Do not rely only on grand visions. Sharp Analyst candidates require clear proof and will see hype as ungrounded.',
      strategy: 'Speak with numbers and structured facts. Bring written commission charts and newcomer success metrics.'
    },
    I_D: {
      dynamic: 'Inspirational Energy ✕ Goal Driven',
      blindSpot:
        'Avoid excessive chit-chat. Decisive Driver candidates value efficiency and will lose patience with long warm-ups.',
      strategy:
        'Get straight to the point in 3 minutes. Outline compensation, promotion pace, and ceiling with crisp clarity.'
    },
    I_I: {
      dynamic: 'Instant Chemistry · High Synergy',
      blindSpot:
        'Great warmth can lack discipline. You risk having an exciting discussion that ends with no concrete next step.',
      strategy: 'Close at the peak of enthusiasm. Lock in the exact date for the follow-up meeting or license prep.'
    },
    I_S: {
      dynamic: 'Inspiring Warmth ✕ Steady Trust',
      blindSpot:
        'Do not depict sudden, drastic lifestyle shifts that intimidate a Steady Guardian candidate about their ability to cope.',
      strategy: 'Center on companionship. Reassure them that team mentors guide each step and they never walk alone.'
    },
    I_C: {
      dynamic: 'Visionary ✕ Analytical Skeptic',
      blindSpot:
        'Do not pitch with pure emotion. Sharp Analyst candidates are skeptical by nature and want factual grounding.',
      strategy:
        'Swap emotions for written details. Walk through verified guidelines and compensation models step by step.'
    },
    S_D: {
      dynamic: 'Supportive Patience ✕ Fierce Drive',
      blindSpot:
        'Avoid sounding overly hesitant or overly polite; Decisive Driver candidates need to see strong leadership confidence.',
      strategy:
        'Be decisive and concise. Tell them clearly what team resources and springboard opportunities you provide.'
    },
    S_I: {
      dynamic: 'Attentive Ear ✕ Expressive Spark',
      blindSpot:
        'Do not be overly quiet or slow-paced. Enthusiastic Sharer candidates thrive on energy and might feel unappreciated.',
      strategy:
        'Boost your verbal warmth. Give immediate positive reinforcement and illustrate their future team popularity.'
    },
    S_S: {
      dynamic: 'Mutual Empathy · Harmonious Rapport',
      blindSpot:
        'Both sides being overly considerate can stall crucial decisions indefinitely without anyone taking initiative.',
      strategy:
        'Take the initiative gently. Propose: "Next Tuesday I will accompany you to meet our director throughout."'
    },
    S_C: {
      dynamic: 'Careful Precision ✕ Evidence-Based',
      blindSpot: 'The conversation can become too reserved, with both sides hesitating to push for definitive action.',
      strategy:
        'Leverage your patient empathy. Answer every question methodically and provide an organized action path.'
    },
    C_D: {
      dynamic: 'Systematic Logic ✕ Big Picture Win',
      blindSpot:
        'Do not get bogged down in administrative technicalities. Decisive Drivers care about the summit, not the paperwork.',
      strategy:
        'Stick to the macro framework and ultimate earnings upside. Leave administrative details for onboarding.'
    },
    C_I: {
      dynamic: 'Analytical Mind ✕ Social Emotion',
      blindSpot:
        'Do not present rigid rules without warmth. Enthusiastic Sharer candidates need human connection and vibrant team culture.',
      strategy:
        'Smile and highlight their interpersonal strengths. Frame structured systems as a protective safety net.'
    },
    C_S: {
      dynamic: 'Orderly Process ✕ Craving Stability',
      blindSpot:
        'Focusing purely on compliance without empathy leaves Steady Guardians feeling insecure and overwhelmed.',
      strategy:
        'Provide certainty through clarity. Explain that every step follows a proven path guarded by dedicated mentors.'
    },
    C_C: {
      dynamic: 'Deep Thinkers · Methodical Analysis',
      blindSpot:
        'Both sides can get stuck in endless comparison loops, falling into analysis paralysis without deciding.',
      strategy:
        'Set a pragmatic trial boundary: "We have evaluated every angle; the lowest-risk validation is a 1-day team session."'
    }
  }
};

/**
 * Smart IDEA 溝通方式 — the same 16 pairings, advising a meeting with a CLIENT
 * about protection and planning rather than a career conversation.
 */
const BUSINESS_CHEMISTRY: Record<ChemistryLocale, Record<string, PairAdvice>> = {
  'zh-Hant-HK': {
    D_D: {
      blindSpot: '切忌與客戶爭論誰對誰錯或強推方案；果斷決策型客戶不喜歡被說服，一旦感到被壓制便會直接拒絕。',
      strategy: '把選擇權交給他！準備兩至三個方案，直接列出成本與效益，由他拍板，你只負責提供專業判斷。'
    },
    D_I: {
      blindSpot: '切忌一開口就是保額、保費與數字表格；熱情分享型客戶會覺得枯燥乏味，還未講到重點已失去興趣。',
      strategy: '先談他的生活與家人，用真實例子說明保障的意義，數字只保留最關鍵的一兩個。'
    },
    D_S: {
      blindSpot: '切忌催促他即場簽單或施加限時壓力；穩健守護型客戶重視安全感，被逼急只會說「再考慮一下」然後不再回覆。',
      strategy: '放慢節奏，先了解他的家庭狀況與擔憂；說明理賠流程與售後跟進，並主動約定下次再談，讓他安心決定。'
    },
    D_C: {
      blindSpot: '切忌只說「一定划算」「回報很好」而拿不出依據；精明分析型客戶會認為你誇大其詞，對整個方案失去信任。',
      strategy: '準備條款、產品說明書與歷史分紅實現率等書面資料，逐項回答他的問題，並給他時間自行比較。'
    },
    I_D: {
      blindSpot: '切忌寒暄太久、話題東拉西扯；果斷決策型客戶重視時間，會認為你不夠專業、浪費他的時間。',
      strategy: '開門見山！三句之內說清楚他的保障缺口、建議方案與成本，然後讓他發問。'
    },
    I_I: {
      blindSpot: '雙方很容易談得十分投契，但會面結束時甚麼都沒有落實，客戶回家後熱情很快冷卻。',
      strategy: '享受氣氛之餘，在他最有興致時敲定下一步：完成需要分析、約定簽單日期，或安排家人一同會面。'
    },
    I_S: {
      blindSpot: '切忌描繪太多極端風險情境或過度渲染；穩健守護型客戶容易感到焦慮與壓力，反而不敢作決定。',
      strategy: '以溫和語氣講解，強調你會長期跟進、隨時支援，讓他感到有人照顧，而不是被推銷。'
    },
    I_C: {
      blindSpot: '切忌憑感覺推介、數字前後不一；精明分析型客戶會記住每個細節，一處出錯便會質疑整個方案。',
      strategy: '收起感性表達，改用對比表格與書面資料，準確回答每個問題；不確定的地方承諾查證後再回覆。'
    },
    S_D: {
      blindSpot: '切忌說話太婉轉、鋪陳太長；果斷決策型客戶會失去耐性，並懷疑你是否具備足夠的專業判斷。',
      strategy: '語氣堅定，直接給出你的專業建議與理由，少用「或者」「可能」，讓他感到你可靠果斷。'
    },
    S_I: {
      blindSpot: '切忌反應太平淡、只顧講解產品；熱情分享型客戶需要互動與認同，太安靜的會面會令他提不起興趣。',
      strategy: '提高對話能量，多回應他的想法與經歷，用生動例子說明方案如何配合他的生活方式與目標。'
    },
    S_S: {
      blindSpot: '雙方都太客氣，誰也不願提出簽單或保費等關鍵話題，跟進拖上幾個月仍然沒有結果。',
      strategy: '主動推進！溫和但清楚地提出下一步：「下星期二我帶齊文件再來，我們一起完成申請。」'
    },
    S_C: {
      blindSpot: '會面容易過於保守、節奏緩慢，雙方都在等對方先作決定，方案一直停留在比較階段。',
      strategy: '耐心逐項解答疑問，然後整理一份清晰的比較摘要與下一步清單，協助他在限期內作出決定。'
    },
    C_D: {
      blindSpot: '切忌拘泥於條款細節或繁瑣流程；果斷決策型客戶關注大局與結果，瑣碎細節會令他感到不耐煩。',
      strategy: '只講核心：保障缺口、方案重點與成本效益；條款細節準備好，他問到才拿出來。'
    },
    C_I: {
      blindSpot: '切忌通篇講條款與數據、缺乏人情味；熱情分享型客戶重視感覺，冰冷的會面會令他覺得你難以親近。',
      strategy: '展現親和力，先建立關係再談方案；把嚴謹的分析化成簡單易明的重點與故事。'
    },
    C_S: {
      blindSpot: '切忌只講條款與數字而忽略他的感受；穩健守護型客戶更在乎的是家人能否得到照顧。',
      strategy: '把方案連繫到他最關心的人，說明每一步都有清晰流程，並承諾日後陪他處理理賠與保單檢視。'
    },
    C_C: {
      blindSpot: '雙方容易陷入無止境的比較與分析，造成「分析癱瘓」，方案遲遲未能落實。',
      strategy: '主動設定決定時限：「我們已比較過所有重點，保障越早生效越好，建議本月內完成申請。」'
    }
  },
  zh: {
    D_D: {
      blindSpot: '切忌与客户争论谁对谁错或强推方案；果断决策型客户不喜欢被说服，一旦感到被压制便会直接拒绝。',
      strategy: '把选择权交给他！准备两至三个方案，直接列出成本与效益，由他拍板，你只负责提供专业判断。'
    },
    D_I: {
      blindSpot: '切忌一开口就是保额、保费与数字表格；热情分享型客户会觉得枯燥乏味，还未讲到重点已失去兴趣。',
      strategy: '先谈他的生活与家人，用真实例子说明保障的意义，数字只保留最关键的一两个。'
    },
    D_S: {
      blindSpot: '切忌催促他当场签单或施加限时压力；稳健守护型客户重视安全感，被逼急只会说「再考虑一下」然后不再回复。',
      strategy: '放慢节奏，先了解他的家庭状况与担忧；说明理赔流程与售后跟进，并主动约定下次再谈，让他安心决定。'
    },
    D_C: {
      blindSpot: '切忌只说「一定划算」「回报很好」而拿不出依据；精明分析型客户会认为你夸大其词，对整个方案失去信任。',
      strategy: '准备条款、产品说明书与历史分红实现率等书面资料，逐项回答他的问题，并给他时间自行比较。'
    },
    I_D: {
      blindSpot: '切忌寒暄太久、话题东拉西扯；果断决策型客户重视时间，会认为你不够专业、浪费他的时间。',
      strategy: '开门见山！三句之内说清楚他的保障缺口、建议方案与成本，然后让他发问。'
    },
    I_I: {
      blindSpot: '双方很容易谈得十分投契，但会面结束时什么都没有落实，客户回家后热情很快冷却。',
      strategy: '享受气氛之余，在他最有兴致时敲定下一步：完成需要分析、约定签单日期，或安排家人一同会面。'
    },
    I_S: {
      blindSpot: '切忌描绘太多极端风险情境或过度渲染；稳健守护型客户容易感到焦虑与压力，反而不敢作决定。',
      strategy: '以温和语气讲解，强调你会长期跟进、随时支援，让他感到有人照顾，而不是被推销。'
    },
    I_C: {
      blindSpot: '切忌凭感觉推介、数字前后不一；精明分析型客户会记住每个细节，一处出错便会质疑整个方案。',
      strategy: '收起感性表达，改用对比表格与书面资料，准确回答每个问题；不确定的地方承诺查证后再回复。'
    },
    S_D: {
      blindSpot: '切忌说话太婉转、铺陈太长；果断决策型客户会失去耐性，并怀疑你是否具备足够的专业判断。',
      strategy: '语气坚定，直接给出你的专业建议与理由，少用「或者」「可能」，让他感到你可靠果断。'
    },
    S_I: {
      blindSpot: '切忌反应太平淡、只顾讲解产品；热情分享型客户需要互动与认同，太安静的会面会令他提不起兴趣。',
      strategy: '提高对话能量，多回应他的想法与经历，用生动例子说明方案如何配合他的生活方式与目标。'
    },
    S_S: {
      blindSpot: '双方都太客气，谁也不愿提出签单或保费等关键话题，跟进拖上几个月仍然没有结果。',
      strategy: '主动推进！温和但清楚地提出下一步：「下星期二我带齐文件再来，我们一起完成申请。」'
    },
    S_C: {
      blindSpot: '会面容易过于保守、节奏缓慢，双方都在等对方先作决定，方案一直停留在比较阶段。',
      strategy: '耐心逐项解答疑问，然后整理一份清晰的比较摘要与下一步清单，协助他在限期内作出决定。'
    },
    C_D: {
      blindSpot: '切忌拘泥于条款细节或繁琐流程；果断决策型客户关注大局与结果，琐碎细节会令他感到不耐烦。',
      strategy: '只讲核心：保障缺口、方案重点与成本效益；条款细节准备好，他问到才拿出来。'
    },
    C_I: {
      blindSpot: '切忌通篇讲条款与数据、缺乏人情味；热情分享型客户重视感觉，冰冷的会面会令他觉得你难以亲近。',
      strategy: '展现亲和力，先建立关系再谈方案；把严谨的分析化成简单易明的重点与故事。'
    },
    C_S: {
      blindSpot: '切忌只讲条款与数字而忽略他的感受；稳健守护型客户更在乎的是家人能否得到照顾。',
      strategy: '把方案联系到他最关心的人，说明每一步都有清晰流程，并承诺日后陪他处理理赔与保单检视。'
    },
    C_C: {
      blindSpot: '双方容易陷入无止境的比较与分析，造成「分析瘫痪」，方案迟迟未能落实。',
      strategy: '主动设定决定时限：「我们已比较过所有重点，保障越早生效越好，建议本月内完成申请。」'
    }
  },
  en: {
    D_D: {
      blindSpot:
        'Never argue over who is right or push a plan hard. Decisive Driver clients hate being talked into things and will simply say no.',
      strategy:
        'Hand them the decision. Bring two or three options with cost and benefit side by side, and let them choose.'
    },
    D_I: {
      blindSpot:
        'Opening with sums assured, premiums and tables bores Enthusiastic Sharer clients before you reach the point.',
      strategy:
        'Start with their life and family, explain protection through real stories, and keep only one or two key numbers.'
    },
    D_S: {
      blindSpot:
        'Pushing for a signature today makes Steady Guardian clients say "let me think about it" and go quiet.',
      strategy:
        'Slow down. Ask about their family and worries, walk through claims and after-sales support, and book the next talk yourself.'
    },
    D_C: {
      blindSpot: 'Calling it "a great deal" without evidence makes Sharp Analyst clients distrust the whole proposal.',
      strategy:
        'Bring the policy terms, product brochure and fulfilment-ratio history, answer point by point, and give them time to compare.'
    },
    I_D: {
      blindSpot: 'Long small talk and wandering topics make Decisive Driver clients feel you are wasting their time.',
      strategy:
        'Get to the point: within three sentences, state their protection gap, your recommendation and the cost, then take questions.'
    },
    I_I: {
      blindSpot:
        'The meeting feels great but nothing gets decided, and the enthusiasm fades once the client gets home.',
      strategy:
        'Enjoy the energy, then lock in the next step at its peak: finish the needs analysis, set a signing date, or meet the family.'
    },
    I_S: {
      blindSpot:
        'Painting dramatic worst-case scenarios makes Steady Guardian clients anxious and less able to decide.',
      strategy:
        'Keep a gentle tone and stress that you will follow up for the long term, so they feel looked after rather than sold to.'
    },
    I_C: {
      blindSpot:
        'Recommending on gut feel or quoting inconsistent numbers makes Sharp Analyst clients doubt the entire plan.',
      strategy:
        'Swap stories for comparison tables and written material; answer precisely, and promise to check anything you are unsure of.'
    },
    S_D: {
      blindSpot:
        'Being too indirect or building up too slowly makes Decisive Driver clients impatient and doubt your judgement.',
      strategy:
        'Be firm. Give your professional recommendation and the reason, skip "maybe" and "perhaps", and sound decisive.'
    },
    S_I: {
      blindSpot:
        'A flat, product-only walkthrough loses Enthusiastic Sharer clients, who need interaction and recognition.',
      strategy:
        'Raise the energy, respond to their ideas and stories, and show vividly how the plan fits their lifestyle and goals.'
    },
    S_S: {
      blindSpot:
        'Both sides are too polite to raise premiums or signing, and follow-up drifts for months without a result.',
      strategy:
        'Take the lead, gently but clearly: "I will bring the forms next Tuesday and we can complete the application together."'
    },
    S_C: {
      blindSpot:
        'The meeting turns cautious and slow; each side waits for the other, and the plan stays stuck in comparison.',
      strategy:
        'Answer each question patiently, then hand over a clear comparison summary and next-step list with a target date.'
    },
    C_D: {
      blindSpot:
        'Dwelling on clauses and process makes Decisive Driver clients, who care about the big picture, impatient.',
      strategy:
        'Cover the core only: the gap, the plan highlights and the cost-benefit. Keep the fine print ready for when they ask.'
    },
    C_I: {
      blindSpot:
        'A meeting of clauses and data with no warmth makes Enthusiastic Sharer clients feel you are hard to connect with.',
      strategy: 'Build rapport before the plan, and turn rigorous analysis into a few simple points and stories.'
    },
    C_S: {
      blindSpot:
        'Talking only terms and numbers misses what Steady Guardian clients care about most: whether their family will be looked after.',
      strategy:
        'Tie the plan to the people they care about, show the clear process, and promise to stand by them for claims and reviews.'
    },
    C_C: {
      blindSpot:
        'Both sides can compare and analyse endlessly, falling into analysis paralysis while the plan never starts.',
      strategy:
        'Set a decision point: "We have compared everything that matters, and cover works best in force early, so let us apply this month."'
    }
  }
};

export const normalizeChemistryLocale = (loc?: string): ChemistryLocale => {
  if (!loc) return 'zh-Hant-HK';
  const lower = loc.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('zh-cn') || lower.startsWith('zh-hans') || lower === 'zh') return 'zh';
  return 'zh-Hant-HK';
};

export const getDiscChemistry = (
  advisorDisc: AdvisorDiscType | null,
  otherDisc: string | null | undefined,
  locale?: string,
  mode: DiscChemistryMode = 'recruit'
): DiscChemistryInfo | null => {
  if (!advisorDisc || !otherDisc) return null;
  const letter = otherDisc.toUpperCase();
  if (!['D', 'I', 'S', 'C'].includes(letter)) return null;

  const key = `${advisorDisc}_${letter}`;
  const lang = normalizeChemistryLocale(locale);
  const recruit = RECRUIT_CHEMISTRY[lang]?.[key] || RECRUIT_CHEMISTRY['zh-Hant-HK'][key];
  if (!recruit) return null;
  if (mode === 'recruit') return recruit;
  const advice = BUSINESS_CHEMISTRY[lang]?.[key] || BUSINESS_CHEMISTRY['zh-Hant-HK'][key];
  return advice ? { dynamic: recruit.dynamic, ...advice } : null;
};
